import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';

export type TransactionFraudFlagDto = {
  id?: string;
  flagType?: string;
  ruleOrReason?: string;
  severity?: string;
  flaggedAt?: string | null;
  isResolved?: boolean;
};

type Props = {
  fraudFlags?: TransactionFraudFlagDto[] | null;
  fraudFlag?: TransactionFraudFlagDto | null;
  className?: string;
};

export function TransactionFraudFlagBanner({ fraudFlags, fraudFlag, className }: Props) {
  const fmt = useFormatDate();
  const flags = (fraudFlags?.length ? fraudFlags : fraudFlag ? [fraudFlag] : []).filter(Boolean);
  if (!flags.length) return null;

  const hasUnresolved = flags.some((flag) => !flag.isResolved);

  return (
    <section
      className={cn(
        'rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950',
        className,
      )}
      role="alert"
      aria-label="Fraud indicator warning"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <h3 className="font-semibold text-amber-950">
              {hasUnresolved ? 'Flagged for review' : 'Previously flagged for review'}
            </h3>
            <p className="mt-1 leading-relaxed text-amber-900/90">
              This transaction triggered a fraud or risk check. Review the reason below before taking
              further action.
            </p>
          </div>

          <ul className="space-y-2">
            {flags.map((flag) => {
              const flaggedAtLabel = flag.flaggedAt ? fmt(flag.flaggedAt) : null;
              return (
                <li
                  key={flag.id || `${flag.flagType}-${flag.flaggedAt}`}
                  className="rounded-md border border-amber-100 bg-white/70 px-3 py-2"
                >
                  <div className="font-medium text-amber-950">
                    {flag.ruleOrReason || 'Needs manual review'}
                  </div>
                  <dl className="mt-2 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
                    {flag.severity ? (
                      <div>
                        <dt className="text-amber-900/75">Severity</dt>
                        <dd className="capitalize text-amber-950">{flag.severity}</dd>
                      </div>
                    ) : null}
                    {flaggedAtLabel ? (
                      <div>
                        <dt className="text-amber-900/75">Flagged at</dt>
                        <dd className="text-amber-950">{flaggedAtLabel}</dd>
                      </div>
                    ) : null}
                    <div>
                      <dt className="text-amber-900/75">Status</dt>
                      <dd className="text-amber-950">{flag.isResolved ? 'Resolved' : 'Unresolved'}</dd>
                    </div>
                  </dl>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

export default TransactionFraudFlagBanner;
